import React, { useState, useCallback, useMemo, useEffect } from "react";
import styles from "./DashboardPage.module.scss";
import { MattersChart, MattersStatsCard } from "./MattersChart";
import TasksGrid from "./TasksGrid";
import { TimeTagetChart, TimeRecordedStatsCard } from "./TimeTargetChart";
import {
  BillingTargetChart,
  BillingTargetStatsCard,
  UnBilledTargetStatsCard,
} from "./BillingTargetChart";
import { DebtorsChart } from "./DebtorsChart";
import { CreditorsChart } from "./CreditorsChart";
import { DashboardCard } from "components/Dashboard";
import MyMattersGrid from "./MyMattersList";
import { FilterComponent } from "components/common/FiltersComponent";
import { Skeleton } from "@progress/kendo-react-indicators";
import { getAccessRightsData } from "services/currentUserService.js";
import { updateDashboardData } from "services/dashboardService";
import LoadingScreen from "components/common/Loading";

const Dashboard = () => {
  const [allowAccessData, setAllowAccessData] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const [filters, setFilters] = useState({
    feeEarnerRef: null,
    branch: null,
    appCode: null,
    teamId: null,
    FromDate: null,
    ToDate: null,
    duration: null,
  });

  useEffect(() => {
    if (!filters?.feeEarnerRef && !filters?.teamId && !filters?.branch) return;
    if (!filters?.FromDate || !filters?.ToDate) return;

    updateDashboardData(filters);
  }, [
    filters?.teamId,
    filters?.feeEarnerRef,
    filters?.branch,
    filters?.FromDate,
    filters?.ToDate,
  ]);

  const analyzeFilterParamters = useCallback((params) => {
    const paramsToFilter = {
      branch: params?.branch,
      appCode: params?.appCode,
      feeEarnerRef: params?.feeEarnerRef,
      teamId: params?.teamId,
      FromDate: params?.FromDate,
      ToDate: params?.ToDate,
      duration: params?.duration,
    };

    // Only update if values actually changed
    setFilters((prev) => {
      const hasChanged = Object.keys(paramsToFilter).some(
        (key) => prev[key] !== paramsToFilter[key],
      );
      return hasChanged ? paramsToFilter : prev;
    });
  }, []);

  const getAccessRights = async () => {
    try {
      const data = await getAccessRightsData();
      setAllowAccessData(data?.data?.data);
      setIsLoading(false);
    } catch (error) {}
  };

  useEffect(() => {
    getAccessRights();
  }, [localStorage.getItem("url")]);

  const dateDifference = useMemo(() => {
    if (!filters.FromDate || !filters.ToDate) return 0;
    return (
      (new Date(filters.ToDate) - new Date(filters.FromDate)) / (1000 * 60 * 60 * 24)
    );
  }, [filters.FromDate, filters.ToDate]);

  const chartColumnClass = useMemo(() => {
    return dateDifference > 7 ? 12 : 4;
  }, [dateDifference]);

  return (
    <div
      className={`container-fluid overflow-auto px-5 mb-5 ${styles.dashboardContainer}`}>
      <div className="mt-2">
        {isLoading ? (
          <div className="row">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className={`col-xl-3 col-6`}>
                <div className="d-flex gap-2 align-items-center">
                  <Skeleton
                    shape="circle"
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                    }}
                  />
                  <Skeleton
                    shape="text"
                    className="rounded"
                    style={{
                      width: "50px",
                      height: "30px",
                    }}
                  />
                </div>

                <Skeleton
                  shape="rectangle"
                  className="mb-3 rounded"
                  style={{
                    width: "100%",
                    height: "50px",
                  }}
                />
              </div>
            ))}
          </div>
        ) : (
          <FilterComponent
            allowAccessData={allowAccessData}
            updateFilters={analyzeFilterParamters}
            component={"Dashboard"}
          />
        )}
      </div>

      {isLoading ? (
        <div className="row mt-2 gy-2 gx-3 justify-content-center">
          <LoadingScreen />
        </div>
      ) : (
        <>
          <div className="row gy-2 gx-3 justify-content-center">
            <div className={`col-xl-3 col-6`}>
              <DashboardCard className={` ${styles.tiles} ${styles.statsTile} `}>
                <TimeRecordedStatsCard />
              </DashboardCard>
            </div>

            <div className={`col-xl-3 col-6`}>
              <DashboardCard className={` ${styles.tiles} ${styles.statsTile} `}>
                <BillingTargetStatsCard />
              </DashboardCard>
            </div>

            <div className={`col-xl-3 col-6`}>
              <DashboardCard className={` ${styles.tiles} ${styles.statsTile} `}>
                <UnBilledTargetStatsCard />
              </DashboardCard>
            </div>

            <div className={`col-xl-3 col-6`}>
              <DashboardCard className={` ${styles.tiles} ${styles.statsTile} `}>
                <MattersStatsCard />
              </DashboardCard>
            </div>
          </div>

          <div className="row gy-2 gx-3 mt-2">
            <div className={`col-xl-${chartColumnClass}`}>
              <DashboardCard className={` ${styles.tiles} ${styles.chartsTile} `}>
                <TimeTagetChart filters={filters} />
              </DashboardCard>
            </div>

            <div className={`col-xl-${chartColumnClass}`}>
              <DashboardCard className={` ${styles.tiles} ${styles.chartsTile} `}>
                <BillingTargetChart filters={filters} />
              </DashboardCard>
            </div>

            <div className={`col-xl-${chartColumnClass}`}>
              <DashboardCard className={` ${styles.tiles} ${styles.chartsTile} `}>
                <MattersChart filters={filters} />
              </DashboardCard>
            </div>
          </div>

          {/*Show Debtors and Creditors if user have accounts license*/}
          <div className="row gy-2 gx-3 mt-2">
            <div className="col-xl-6 col-12">
              <DashboardCard className={` ${styles.tiles} ${styles.chartsTile} `}>
                <DebtorsChart filters={filters} />
              </DashboardCard>
            </div>

            <div className="col-xl-6 col-12">
              <DashboardCard className={` ${styles.tiles} ${styles.chartsTile} `}>
                <CreditorsChart filters={filters} />
              </DashboardCard>
            </div>
          </div>

          <div className="row gy-2 gx-3 mt-2">
            <div className="col-xl-7 col-12">
              <DashboardCard className={`${styles.tiles} ${styles.gridTile}`}>
                <MyMattersGrid />
              </DashboardCard>
            </div>

            <div className="col-xl-5 col-12">
              <DashboardCard className={`${styles.tiles} ${styles.gridTile}`}>
                <TasksGrid />
              </DashboardCard>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
